/**
 * Test DeepSearch REST Access
 * 
 * Verifies that:
 * 1. Allowed tables are accessible
 * 2. Blocked tables are NOT accessible
 * 3. RLS policies enforce user_id filtering
 */

import { supabase } from '../lib/supabase';

export async function testDeepsearchAccess(userId) {
  console.log('🧪 Testing DeepSearch REST Access...\n');
  
  const results = {
    allowed: [],
    blocked: [],
    errors: []
  };

  // Test 1: Verify access to ALLOWED tables
  const allowedTables = ['items', 'uploads', 'item_embeddings', 'item_interactions'];
  
  console.log('✅ Testing ALLOWED tables:');
  for (const table of allowedTables) {
    try {
      const { data, error } = await supabase
        .schema('deepsearch')
        .from(table)
        .select('*')
        .limit(1);
      
      if (error) {
        console.log(`  ❌ ${table}: ${error.message}`);
        results.errors.push({ table, error: error.message });
      } else {
        console.log(`  ✅ ${table}: Access granted (${data?.length || 0} rows)`);
        results.allowed.push(table);
      }
    } catch (err) {
      console.log(`  ❌ ${table}: ${err.message}`);
      results.errors.push({ table, error: err.message });
    }
  }

  console.log('\n❌ Testing BLOCKED tables (should fail):');
  const blockedTables = ['ingest_queue', 'metrics', 'queries'];
  
  for (const table of blockedTables) {
    try {
      const { data, error } = await supabase
        .schema('deepsearch')
        .from(table)
        .select('*')
        .limit(1);
      
      if (error) {
        // This is EXPECTED - table should be blocked
        console.log(`  ✅ ${table}: Correctly blocked (${error.message})`);
        results.blocked.push(table);
      } else {
        // This is BAD - table should NOT be accessible
        console.log(`  ⚠️  ${table}: SECURITY ISSUE - Table is accessible!`);
        results.errors.push({ table, error: 'Table should be blocked but is accessible' });
      }
    } catch (err) {
      // This is also expected
      console.log(`  ✅ ${table}: Correctly blocked (${err.message})`);
      results.blocked.push(table);
    }
  }

  // Test 2: Verify RLS enforcement (if userId provided)
  if (userId) {
    console.log('\n🔒 Testing RLS enforcement:');
    try {
      // Try to query items - should only return user's own items
      const { data, error } = await supabase
        .schema('deepsearch')
        .from('items')
        .select('user_id')
        .limit(10);
      
      if (error) {
        console.log(`  ❌ RLS test failed: ${error.message}`);
      } else {
        const allOwnRows = data.every(item => item.user_id === userId);
        if (allOwnRows) {
          console.log(`  ✅ RLS working: All ${data.length} rows belong to current user`);
        } else {
          console.log(`  ⚠️  RLS ISSUE: Found rows from other users!`);
          results.errors.push({ 
            table: 'items', 
            error: 'RLS not enforcing user_id filter' 
          });
        }
      }
    } catch (err) {
      console.log(`  ❌ RLS test error: ${err.message}`);
    }
  }

  // Summary
  console.log('\n📊 Test Summary:');
  console.log(`  ✅ Allowed tables accessible: ${results.allowed.length}/${allowedTables.length}`);
  console.log(`  ❌ Blocked tables (correctly): ${results.blocked.length}/${blockedTables.length}`);
  console.log(`  ⚠️  Errors/Issues: ${results.errors.length}`);

  if (results.errors.length > 0) {
    console.log('\n⚠️  Issues found:');
    results.errors.forEach(({ table, error }) => {
      console.log(`  - ${table}: ${error}`);
    });
  } else {
    console.log('\n✅ All security checks passed!');
  }

  return results;
}

// Expose globally for console testing
if (typeof window !== 'undefined') {
  window.testDeepsearchAccess = testDeepsearchAccess;
  console.log('🧪 Test available: window.testDeepsearchAccess(userId)');
}





